import type { CarouselImageProperty } from '../ProductImage';
import type { Dispatch, SetStateAction, RefObject } from 'react';

import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';
import { useEffect, useCallback } from 'react';

import type { Maybe, Video } from '~/utilities/graphql/codegen';

interface UseCarouselEventsProps {
  // State setters
  setCurrentImageIndex: Dispatch<SetStateAction<number>>;
  stopTimer: Dispatch<SetStateAction<boolean>>;

  // State values
  isMobile: boolean;
  isAutoSlide: boolean;
  timerStopped: boolean;
  currentImageIndex: number;
  isSwipeMode: boolean;
  scrollInProgress: number[];

  // Refs
  galleryRef: RefObject<HTMLUListElement | null>;
  itemRefs: RefObject<(HTMLLIElement | null)[]>;

  // Data
  productImageList: CarouselImageProperty[];
  video?: Maybe<Video>;

  // Functions
  slideToNextImage: (newIndex: number) => void;
}

export const useCarouselEvents = ({
  setCurrentImageIndex,
  stopTimer,
  isMobile,
  isAutoSlide,
  timerStopped,
  currentImageIndex,
  isSwipeMode,
  scrollInProgress,
  galleryRef,
  itemRefs,
  productImageList,
  video,
  slideToNextImage,
}: UseCarouselEventsProps) => {
  useEffect(() => {
    gsap.registerPlugin(ScrollToPlugin);
  }, []);

  // Auto-slide effect
  useEffect(() => {
    const autoSlideToNextImg = () => {
      const nextIndex = (currentImageIndex + 1) % productImageList.length;

      slideToNextImage(nextIndex);
    };

    let timerId = 0;
    const delay = isMobile && video && currentImageIndex === 2 ? 18000 : 5000;

    if (isAutoSlide && !timerStopped) {
      timerId = window.setTimeout(autoSlideToNextImg, delay);
    }

    return () => {
      if (timerId !== 0) {
        window.clearTimeout(timerId);
      }
    };
  }, [
    currentImageIndex,
    isAutoSlide,
    productImageList.length,
    slideToNextImage,
    timerStopped,
    isMobile,
    video,
  ]);

  // IntersectionObserver for swipe mode
  useEffect(() => {
    if (!isSwipeMode) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        const intersectingEntry = entries.find(entry => entry.isIntersecting);

        if (intersectingEntry) {
          const index = itemRefs.current?.findIndex(ref => ref === intersectingEntry.target);

          if (index !== undefined && index !== -1 && !scrollInProgress.length) {
            setCurrentImageIndex(prevIndex => {
              if (prevIndex !== index) {
                stopTimer(true);

                return index;
              }

              return prevIndex;
            });
          }
        }
      },
      {
        root: galleryRef.current,
        threshold: 0.6,
      }
    );

    const currentItemRefs = itemRefs.current;

    if (currentItemRefs) {
      currentItemRefs.forEach(ref => {
        if (ref) {
          observer.observe(ref);
        }
      });
    }

    return () => {
      if (currentItemRefs && observer) {
        currentItemRefs.forEach(ref => {
          if (ref) {
            observer.unobserve(ref);
          }
        });
      }
    };
  }, [isSwipeMode, setCurrentImageIndex, stopTimer, scrollInProgress, galleryRef, itemRefs]);

  // Touch end handler
  const handleTouchEnd = useCallback(() => {
    if (isSwipeMode && !timerStopped) {
      stopTimer(true);
    }
  }, [isSwipeMode, timerStopped, stopTimer]);

  return {
    handleTouchEnd,
  };
};
